import { useState } from 'react';
import { Box, Button, List, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FileItem, downloadFileFromUrl } from '@/common';
import { downloadFileFromResponse, getMessageFromError } from '@/core';
import { downloadFileFromAuditorVisit } from '../api/downloadFileFromAuditorVisit';
import FileUploadModal from './FileUploadModal';
import { useAuditorVisitDetailContext } from '../contexts/AuditorVisitDetailContext';
import { downloadFileFromDws } from '../api/downloadFileFromDs';
import { checkIfErrorIsAccessAuthorizationError } from '@/core/utils/getMessageFromError';

interface FilesProps {
  isTemplates?: boolean;
}

const Files = ({ isTemplates }: FilesProps) => {
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { auditorVisit, invalidateAuditorVisit } = useAuditorVisitDetailContext();

  const downloadFileMutation = useMutation({
    mutationFn: downloadFileFromAuditorVisit,
    onSuccess: async (response) => {
      await downloadFileFromResponse(response);
      enqueueSnackbar(
        t('Successfully downloaded file.', { defaultValue: 'Successfully downloaded file.' }),
        { variant: 'success' }
      );
    },
    onError: (error: Error) => {
      checkIfErrorIsAccessAuthorizationError(error)
        ? enqueueSnackbar(
            t('No access authorisation!', { defaultValue: 'No access authorisation!' }),
            { variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'center' } }
          )
        : enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  const downloadFileFromDsMutation = useMutation({
    mutationFn: downloadFileFromDws,
    onSuccess: async (result) => {
      const res = result.res;
      const blob = await res.blob();
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', result.filename);

      // Append to html link element page
      document.body.appendChild(link);

      // Start download
      link.click();

      enqueueSnackbar(
        t('Successfully downloaded file.', { defaultValue: 'Successfully downloaded file.' }),
        { variant: 'success' }
      );
    },
    onError: (error: Error) => {
      checkIfErrorIsAccessAuthorizationError(error)
        ? enqueueSnackbar(
            t('No access authorisation!', { defaultValue: 'No access authorisation!' }),
            { variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'center' } }
          )
        : enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  return (
    <>
      <FileUploadModal show={showFileUploadModal} setShow={setShowFileUploadModal} />

      {!isTemplates && (
        <Box>
          {auditorVisit.documents.length > 0 ? (
            <List>
              {auditorVisit.documents.map((item) => (
                <FileItem
                  key={item.id}
                  name={item.title || ''}
                  uploadedBy={item.uploadedBy}
                  uploadedAt={item.uploadedAt}
                  storeType={item.storeType}
                  fileName={item.fileName}
                  onDownload={() => {
                    if (item.storeType === 'DocumentSystem' && item.dwsDok) {
                      downloadFileFromDsMutation.mutate({
                        dok: item.dwsDok,
                        filename: item.title || item.dwsDok.originalfilename,
                      });
                    } else {
                      downloadFileMutation.mutate({ id: auditorVisit.id, fileId: item.id });
                    }
                  }}
                />
              ))}
            </List>
          ) : (
            <Typography variant="subtitle1">
              {t('No files', { defaultValue: 'No files' })}
            </Typography>
          )}
        </Box>
      )}

      {isTemplates && (
        <Box>
          {auditorVisit.documentsGen.length > 0 ? (
            <List>
              {auditorVisit.documentsGen.map((item) => (
                <FileItem
                  key={item.id}
                  name={item.title || ''}
                  uploadedBy={item.uploadedBy}
                  uploadedAt={item.uploadedAt}
                  storeType={item.storeType}
                  fileName={item.fileName}
                  onDownload={() => {
                    if (item.storeType === 'DocumentSystem' && item.dwsDok) {
                      downloadFileFromDsMutation.mutate({
                        dok: item.dwsDok,
                        filename: item.title || item.dwsDok.originalfilename,
                      });
                    } else {
                      downloadFileMutation.mutate({ id: auditorVisit.id, fileId: item.id });
                    }
                  }}
                />
              ))}
            </List>
          ) : (
            <Typography variant="subtitle1">
              {t('No files', { defaultValue: 'No files' })}
            </Typography>
          )}
        </Box>
      )}
      {!isTemplates && (
        <Button
          onClick={() => setShowFileUploadModal(true)}
          variant="outlined"
          startIcon={<AddIcon />}
          fullWidth
        >
          {t('Add files', { defaultValue: 'Add files' })}
        </Button>
      )}
    </>
  );
};

export default Files;
